import {
  Bar,
  BarChart as RechartsBarChart,
  LabelList,
  ReferenceLine,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';

import type { BarChartProps } from './bar-chart';
import {
  BAR_CHART_CATEGORY_KEY,
  barChartSeriesKey,
  buildBarChartAriaLabel,
  resolveSeriesLabel,
  resolveSeriesValue,
  resolveTooltipSeriesLabel,
  toBarChartRows,
} from './bar-chart-data';
import { BarChartHorizontalBar } from './bar-chart-horizontal-bar';
import { BarChartLegend } from './bar-chart-legend';
import { BarChartValueLabel } from './bar-chart-value-label';
import { BarChartYAxisTick } from './bar-chart-y-axis-tick';
import { getSeriesSignSummary } from '../shared/chart-helpers';
import {
  CHART_AXIS,
  CHART_DIMENSIONS,
  CHART_HORIZONTAL_DIMENSIONS,
  CHART_VALUE_LABEL_TEXT,
} from '../shared/chart-tokens';
import { InvalidChart } from '../shared/invalid-chart';
import { validateBarChartProps } from '../shared/chart-validation';

export type BarChartHorizontalProps = Omit<
  BarChartProps,
  'maxBarSize' | 'showValueLabels'
>;

/** Dynamic height for horizontal bar charts (grows with categories; no inner scroll). */
export const resolveHorizontalChartHeightPx = (
  categoryCount: number,
  seriesCount: number
): number => {
  const {
    barThicknessPx,
    barGapPx,
    categoryPaddingPx,
    chartVerticalPaddingPx,
  } = CHART_HORIZONTAL_DIMENSIONS;

  const rowHeightPx =
    seriesCount * barThicknessPx +
    Math.max(0, seriesCount - 1) * barGapPx +
    categoryPaddingPx;

  return categoryCount * rowHeightPx + chartVerticalPaddingPx * 2;
};

export const BarChartHorizontal = ({
  data,
  title,
  animate = true,
  className,
}: BarChartHorizontalProps) => {
  const validation = validateBarChartProps(data);

  if (!validation.valid) {
    return <InvalidChart className={className} />;
  }

  const { normalized } = validation;
  const signSummary = getSeriesSignSummary(normalized.series);
  const dataCrossesZero = signSummary.hasPositive && signSummary.hasNegative;
  const chartData = toBarChartRows(normalized);
  const seriesCount = normalized.series.length;
  const categoryCount = normalized.categoryAxis.categories.length;
  const chartHeightPx = resolveHorizontalChartHeightPx(
    categoryCount,
    seriesCount
  );

  const {
    barThicknessPx,
    barGapPx,
    categoryPaddingPx,
    chartVerticalPaddingPx,
    valueLabelRightMarginPx,
    labelBarGapPx,
  } = CHART_HORIZONTAL_DIMENSIONS;

  return (
    <div className={className}>
      <BarChartLegend series={normalized.series} className="mt-3" />
      <ResponsiveContainer width="100%" height={chartHeightPx}>
        <RechartsBarChart
          layout="vertical"
          data={chartData}
          margin={{
            top: chartVerticalPaddingPx,
            right: CHART_DIMENSIONS.marginRight + valueLabelRightMarginPx,
            left: CHART_DIMENSIONS.marginLeft,
            bottom: chartVerticalPaddingPx,
          }}
          barGap={barGapPx}
          barCategoryGap={categoryPaddingPx}
          accessibilityLayer={true}
          aria-label={buildBarChartAriaLabel(normalized, title)}
        >
          {/* Screen readers announce bar values during keyboard navigation. */}
          <Tooltip
            trigger="click"
            formatter={(value, _name, item) =>
              resolveTooltipSeriesLabel(value, item, normalized.format)
            }
            wrapperClassName="sr-only"
            cursor={false}
          />
          <XAxis type="number" hide />
          <YAxis
            type="category"
            dataKey={BAR_CHART_CATEGORY_KEY}
            axisLine={false}
            tickLine={false}
            width="auto"
            tickMargin={labelBarGapPx}
            interval={0}
            tick={<BarChartYAxisTick />}
          />
          {dataCrossesZero && (
            <ReferenceLine x={0} stroke={CHART_AXIS.zeroLineColor} />
          )}
          {normalized.series.map((singleSeries, seriesIndex) => {
            const dataKey = barChartSeriesKey(seriesIndex);

            return (
              <Bar
                key={dataKey}
                dataKey={dataKey}
                name={singleSeries.label}
                isAnimationActive={animate}
                barSize={barThicknessPx}
                shape={(shapeProps) => {
                  const { value, payload, ...rest } = shapeProps;

                  return (
                    <BarChartHorizontalBar
                      {...rest}
                      value={resolveSeriesValue(
                        { value, payload },
                        seriesIndex
                      )}
                      seriesIndex={seriesIndex}
                      seriesCount={seriesCount}
                    />
                  );
                }}
              >
                <LabelList
                  valueAccessor={(entry) =>
                    resolveSeriesLabel(entry, seriesIndex, normalized.format)
                  }
                  position="right"
                  content={BarChartValueLabel}
                  {...CHART_VALUE_LABEL_TEXT}
                />
              </Bar>
            );
          })}
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};
